<template>
  <ClientOnly>
    <el-popover
      popper-class="datepicker-popover"
      trigger="click"
      width="auto"
      :transition="'el-fade-in-linear'"
      :show-arrow="false"
      @hide="onPopoverClose"
    >
      <template #reference>
        <PrimaryButton data-target="date range" variant="secondary">
          <span class="flex items-center justify-center gap-2">
            <font-awesome-icon
              class="text-base mr-2"
              :icon="['far', 'calendar-range']"
            />
            <p>{{ startDateRange }} - {{ endDateRange }}</p>
          </span>
        </PrimaryButton>
      </template>
      <div
        class="flex w-full justify-between items-center gap-4 px-8 py-4 border-b border-accent-300 text-accent-950"
      >
        <font-awesome-icon
          class="cursor-pointer mr-4 text-2xl text-accent-500"
          :icon="['fal', 'angle-left']"
          @click="paginateBackward()"
        />
        <PrimaryButton
          v-for="opt in calendarOptions"
          :key="opt.label"
          :variant="!opt.isSelected ? 'secondary' : 'primary'"
          class="!px-7 !py-2 text-xs flex-1 min-w-0 date-range-selection"
          :custom-class="
            !opt.isSelected
              ? 'hover:bg-success-200 !text-accent-950 !border-accent-300'
              : ''
          "
          @on-click="setDateRange(opt)"
        >
          {{ t(`sharedComponents.datepicker.${opt.label}`) }}
        </PrimaryButton>
        <font-awesome-icon
          class="cursor-pointer ml-4 text-2xl text-accent-500"
          :icon="['fal', 'angle-right']"
          @click="paginateForward()"
        />
      </div>
      <div class="w-full flex justify-center pt-5 px-3">
        <VDatePicker
          ref="datepicker"
          v-model.range="range"
          class="datepicker"
          mode="date"
          :columns="2"
          :attributes="computedAttributes"
          :max-date="new Date()"
          :locale="userLocale"
          expanded
          borderless
          @dayclick="onDayClick"
        />
      </div>
      <div v-if="!!errorMessage" class="w-full pb-6 px-5 text-danger-600">
        {{ t(errorMessage) }}
      </div>
    </el-popover>
  </ClientOnly>
</template>

<script setup lang="ts">
import { ref, Ref, computed } from "vue";
import { addDays, startOfDay, endOfDay, subYears } from "date-fns";
import { useI18n } from "vue-i18n";
import DateRange from "~/types/DateRangeType";
import { formatDate } from "~/utils/DateFormatter";
import PrimaryButton from "~/components/shared/primary-button/PrimaryButton.vue";

interface CalendarOption {
  label: string;
  isSelected: boolean;
  range: DateRange;
}

const props = defineProps({
  startDate: {
    type: Date,
    required: true,
  },
  endDate: {
    type: Date,
    required: true,
  },
  errorMessage: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["update", "onPopoverClose"]);
const datepicker: Ref = ref(null);
const { t } = useI18n();

const userLocale = computed(() => {
  if (process.client) {
    const lang = localStorage.getItem("language");
    // Convert to format v-calendar expects (e.g., 'fr-FR', 'en-US')
    if (lang) {
      const parts = lang.split("-");
      return parts.length === 2
        ? `${parts[0]}-${parts[1].toUpperCase()}`
        : "en-US";
    }
  }
  return "en-US";
});

const completedRangeSelection = ref(true); // true = second date click sets
const calendarOptions: Ref<Array<CalendarOption>> = ref([
  {
    label: "today",
    isSelected: false,
    range: { start: startOfDay(new Date()), end: new Date() },
  },
  {
    label: "yesterday",
    isSelected: false,
    range: {
      start: startOfDay(addDays(new Date(), -1)),
      end: endOfDay(addDays(new Date(), -1)),
    },
  },
  {
    label: "week",
    isSelected: false,
    range: {
      start: startOfDay(addDays(new Date(), -7)),
      end: new Date(),
    },
  },
  {
    label: "month",
    isSelected: false,
    range: {
      start: startOfDay(addDays(new Date(), -30)),
      end: new Date(),
    },
  },
  {
    label: "twoMonths",
    isSelected: false,
    range: {
      start: startOfDay(addDays(new Date(), -60)),
      end: new Date(),
    },
  },
  {
    label: "year",
    isSelected: false,
    range: {
      start: startOfDay(subYears(new Date(), 1)),
      end: new Date(),
    },
  },
]);

const computedAttributes = computed(() => {
  const attributes: any[] = [];

  // If date range is selected, we highlight the start and end dates
  // and apply a different style to the middle dates.
  if (completedRangeSelection.value) {
    const start = range.value.start;
    const end = range.value.end;

    if (!start || !end) return [];

    const startDate = startOfDay(new Date(start));
    const endDate = startOfDay(new Date(end));

    // Get middle dates (excluding start/end)
    const middleDates: Date[] = [];
    let current = new Date(startDate.getTime());
    current.setDate(current.getDate() + 1);

    const endCopy = new Date(end);
    endCopy.setHours(0, 0, 0, 0);

    while (current < endCopy) {
      middleDates.push(new Date(current));
      current = new Date(current.setDate(current.getDate() + 1));
    }
    // Add start and end dates to attributes
    attributes.push({
      key: "start-end",
      dates: [startDate, endDate], // start and end dates
      highlight: {
        class: "force-sky-blue-highlight",
      },
    });
    // Middle dates — apply only text style, no highlight!
    attributes.push({
      key: "middle",
      dates: middleDates,
      highlight: {
        class: "force-light-sky-blue-highlight", // background
      },
      content: {
        class: "force-light-sky-blue-text", // text style
      },
    });
  }

  // Highlight today only when it is not within the selected date range
  const today = startOfDay(new Date());
  if (today < range.value.start || today > range.value.end) {
    attributes.push({
      key: "today",
      dates: [today],
      dot: {
        class: "!bg-secondary-700", // dot color for today
      },
    });
  }

  return attributes;
});

const startDateRange = computed(() => {
  return formatDate(range.value.start);
});

const endDateRange = computed(() => {
  return formatDate(range.value.end);
});

const paginateForward = async () => {
  // Move forwards 1 month (wait for transition)
  await datepicker.value.moveBy(1);
};

const paginateBackward = async () => {
  // Move backwards 1 month (wait for transition)
  await datepicker.value.moveBy(-1);
};

const range = computed({
  get() {
    return { start: props.startDate, end: props.endDate };
  },
  set(newRange) {
    emit("update", {
      start: newRange.start,
      end: startOfDay(newRange.end),
    });
  },
});

const setDateRange = async (opt: CalendarOption) => {
  for (const option of calendarOptions.value) {
    option.isSelected = false;
  }
  opt.isSelected = true;
  datepicker.value.updateValue({
    start: opt.range.start,
    end: opt.range.end,
  });
  await datepicker.value.move(opt.range.start);
};

const onPopoverClose = () => {
  emit("onPopoverClose");
};

// Update when user interacts with calendar
const onDayClick = () => {
  completedRangeSelection.value = !completedRangeSelection.value; // Toggle selection state
};
</script>

<style scoped>
.onc-btn {
  @apply text-xs !text-accent-950;
}
.onc-btn:hover {
  @apply bg-success-200 text-accent-950;
}
.onc-btn.isActive {
  @apply bg-success-400 !text-white;
}
</style>

<style>
@import url("https://fonts.googleapis.com/css?family=Inter:300,400,700&display=swap");

.datepicker-popover {
  @apply !p-0 !rounded-[16px] !shadow-popover;
  font-family: Inter, sans-serif;
}
/*
    This unscoped css disables the use of the datepickers built-in
    pagination buttons. Ideally we'd want to modify some prop or attribute
    within the component to do this, but this was the best solution I could find.
*/
.vc-container .vc-pane-container .vc-pane-header-wrapper .vc-header .vc-arrow {
  @apply opacity-0 pointer-events-none;
}

/* Override highlight color for start and end date */
.force-sky-blue-highlight {
  background-color: #0369a1 !important; /* Tailwind Sky Blue - Secondary-700 */
  border-radius: 4px !important;
  opacity: 1 !important;
}

/* Override highlight color for middle dates */
.force-light-sky-blue-highlight {
  background-color: #e0f2fe !important; /* Tailwind Sky Blue - Secondary-100 */
  border-radius: 4px !important;
  opacity: 1 !important;
}

/* Override text color for middle dates */
.force-light-sky-blue-text {
  color: #0369a1 !important; /* Tailwind Sky Blue - Secondary-700 */
}

/* Override font apply to all date cells */
.vc-day-content {
  font-family: "Inter", sans-serif !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  letter-spacing: 0 !important;
}

/* Override in-between fill color for selected dates */
.vc-highlight-bg-light {
  background-color: #ebf1ff !important; /* primary-lighter */
}

/* Override date focus shapes */
.vc-day-content.vc-focus {
  border-radius: 4px !important;
}

/* Override circle (outline) after selecting the date */
.vc-highlight-bg-outline {
  border-radius: 4px !important;
  border-color: #0369a1 !important; /* Tailwind Sky Blue - Secondary-700 */
}
</style>
